import { AlgoliaEntity } from '@marmon/shared/types/algolia';
import { PageSize, SortDir } from '@marmon/shared/types/pagination';

export type AlgoliaPaginatedItem<T extends Partial<AlgoliaEntity>> = {
	data: T;
	objectID: string;
};

export type AlgoliaPaginatedOptions<T extends Partial<AlgoliaEntity>> = {
	sortBy: keyof T | null;
	sortDir: SortDir;
};

export interface AlgoliaPaginatedObjectType<
	T extends Partial<AlgoliaEntity>,
> extends AlgoliaPaginatedOptions<T> {
	pageSize: PageSize;
	page: number;
	totalElements: number | null;
	items: AlgoliaPaginatedItem<T>[];
}

export class AlgoliaPaginatedObject<
	T extends Partial<AlgoliaEntity>,
> implements AlgoliaPaginatedObjectType<T> {
	pageSize: PageSize = 10;
	page = 0;

	totalElements: number | null = null;

	sortBy: keyof T | null = 'createdAt';
	sortDir: SortDir = 'desc';

	items: AlgoliaPaginatedItem<T>[] = [];

	constructor(paginated?: Partial<AlgoliaPaginatedObjectType<T>>) {
		Object.assign(this, PaginatedDefault(), paginated);
	}

	get hasMore() {
		if (!this.totalElements) return false;
		return this.items.length < this.totalElements;
	}

	update(paginated: Partial<AlgoliaPaginatedObjectType<T>>) {
		Object.assign(this, paginated);
		return this;
	}

	add(items: AlgoliaPaginatedItem<T>[]) {
		this.items.push(...items);
		return this;
	}

	reset() {
		Object.assign(this, PaginatedDefault());
		return this;
	}
}

const PaginatedDefault = (): Partial<
	AlgoliaPaginatedObjectType<Partial<AlgoliaEntity>>
> => ({
	items: [],
	pageSize: 10,
	page: 0,
	totalElements: null,
	sortBy: 'createdAt',
	sortDir: 'asc',
});
